import React from 'react';
import Link from 'next/link';
import Modal from '@common/modal';
import Button from '@components/common/button';

import { useToast } from '@components/common/toast';
import Icon from '@components/common/assets';
import { useAdditionalItems, useApplyPosition } from '@repository/match';
import { JDModalType } from '@components/position/jd/apply/modals';
import {
  useAdditionInfoHookFormContext,
  useAdditionalInputContext,
} from '@components/position/jd/apply/modals/additionalInputModal/AdditionalInputContext';
import LeaveModal from '@components/position/jd/apply/modals/LeaveModal';
import { useJD } from '@repository/position/usePosition';
import Colors from '@domain/constant/colors';
import { V2RouteUrl } from '@utils/routeUtil';

import useModal from '@utils/hooks/useModal';
import { isEmpty } from '@utils/common';
import classnames from 'classnames/bind';
import styles from './modals.module.scss';
const cx = classnames.bind(styles);

interface ProfileApplyModalProps {
  visible: boolean;
  onClose: () => void;
  setModalState: (modalState: JDModalType) => void;
  positionSn: number;
}

const ProfileApplyModal = ({ visible, onClose, setModalState, positionSn }: ProfileApplyModalProps) => {
  const Toast = useToast();
  const { isComplete: isAdditionInfoComplete, formData, tempFormData, clearForm } = useAdditionalInputContext();
  const { data: additionalItems } = useAdditionalItems(positionSn);
  const { refetch } = useJD(positionSn);
  const isAdditionalExist = !!additionalItems?.isAdditionalExist;
  const { reset, setValue, getValues } = useAdditionInfoHookFormContext();
  const { closeModal: closeLeaveModal, isModalOpen, openModal: openLeaveModal } = useModal();

  const { mutateAsync: applyPositionAsync, isLoading } = useApplyPosition({
    onSuccess: () => {
      refetch();
      setModalState('APPLY_SUCCESS');
    },
    onError: (err) => {
      let content: string;

      switch (err?.response?.data?.errorCode) {
        case 'B902':
          content = '포지션이 삭제되었습니다.';
          break;
        case 'B903':
          content = '포지션이 종료되었습니다.';
          break;
        case 'B904':
          content = '포지션이 마감되었습니다.';
          break;
        case 'B905':
          content = '이미 지원한 포지션 입니다.';
          break;
        case 'B906':
          content = '이미 채용 전형이 진행중인 포지션입니다.';
          break;
        case 'B907': //지원 가능한 횟수를 초과하였습니다.
          setModalState('APPLY_EXCEED');
          return;
        case 'B908':
          Toast({
            type: 'error',
            iconType: 'info',
            content: '지원 조건이 변경되었습니다. 다시 지원해 주세요.',
          });
          onCloseAllModal();
          return;
        case 'B909':
          setModalState('CONTRACT_END');
          return;
        case 'B002': // 데이터를 찾을 수 없습니다.
        case 'B901': //통신 오류
        default:
          setModalState('ERROR');
          return;
      }

      Toast({
        type: 'error',
        iconType: 'info',
        content,
      });
      handleClose();
    },
  });

  const handleApply = async () => {
    const rq = {
      positionSn,
      additionInfo: formData,
    };
    await applyPositionAsync(rq);
  };

  const handleClose = () => {
    if (isEmpty(tempFormData)) {
      reset();
      clearFormField();
      onClose();
      clearForm();
    } else {
      openLeaveModal();
    }
  };

  const onCloseAllModal = () => {
    reset();
    clearFormField();
    clearForm();
    closeLeaveModal();
    onClose();
  };

  const clearFormField = () => {
    const data = getValues();
    Object.keys(data).forEach((key) => {
      setValue(key as never, null as never);
    });
  };

  return (
    <Modal visible={visible} onClickBg={handleClose}>
      <section className={cx('profileApplyModal')}>
        <a className={cx('btnClose')} role="button" onClick={handleClose}>
          <Icon name="closeLight" width={32} height={32} />
        </a>

        <h2 className={cx('title')}>지원하기</h2>

        <ul className={cx('description')}>
          <li className={cx('content')}>
            <Icon name="checkLight" width={24} height={24} />
            잡다에서 작성한 내 프로필로 간단히 지원할 수 있어요.
          </li>
          <li className={cx('content')}>
            <Icon name="checkLight" width={24} height={24} />
            제출해야 하는 정보를 꼼꼼히 확인해 주세요.
          </li>
        </ul>

        <div className={cx('cardWrapper')}>
          <Link className={cx('profileCard')} href={V2RouteUrl.PROFILE} target="_blank">
            <div className={cx('cardTitle')}>
              내 프로필
              <Icon name="arrowRightLight" width={24} height={24} />
            </div>
            <div className={cx('cardStatus')}>
              <Icon name="checkCircleLightFilled" width={32} height={32} fill={Colors.C_GREEN_150} />
              완료
            </div>
          </Link>

          {isAdditionalExist && (
            <button className={cx('profileCard')} onClick={() => setModalState('ADDITIONAL_INPUT')}>
              <div className={cx('cardTitle')}>
                추가 정보
                <Icon name="arrowRightLight" width={24} height={24} />
              </div>
              <div className={cx('cardStatus')}>
                <AdditionalInfoCardStatus isRequired={!isAdditionInfoComplete} />
              </div>
            </button>
          )}
        </div>

        <div className={cx('buttons')}>
          <Button className={cx('button')} onClick={() => setModalState('PREVIEW_PROFILE')} theme="secondary">
            제출 전 미리보기
          </Button>
          <Button
            className={cx('button')}
            onClick={handleApply}
            disabled={!isAdditionalExist ? false : !isAdditionInfoComplete}
            isLoading={isLoading}
          >
            제출하기
          </Button>
        </div>
      </section>

      {/* 입력 후 나가시겠어요? 모달 */}
      <LeaveModal visible={isModalOpen} onClose={closeLeaveModal} onOk={onCloseAllModal} />
    </Modal>
  );
};

export default ProfileApplyModal;

const AdditionalInfoCardStatus = ({ isRequired }: { isRequired: boolean }) => {
  return isRequired ? (
    <>
      <Icon name="errorLightFilled" width={32} height={32} fill={Colors.C_ERROR} />
      미완료
    </>
  ) : (
    <>
      <Icon name="checkCircleLightFilled" width={32} height={32} fill={Colors.C_GREEN_150} />
      완료
    </>
  );
};
